/**
 * SocialGrid — Instagram-style 3×N photo grid with a follow CTA.
 * Visual-first social proof; takes a static images array.
 *
 * Registered as: social / social-grid
 */
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface GridImage {
  src: string;
  alt?: string;
}

interface SocialGridProps {
  headline?: string;
  subheadline?: string;
  instagramHandle?: string;
  instagramUrl?: string;
  images?: GridImage[];
}

const PLACEHOLDER_IMAGES: GridImage[] = Array.from({ length: 9 }, (_, i) => ({
  src: "",
  alt: `Photo ${i + 1}`,
}));

function InstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden>
      <path d="M12 2c2.717 0 3.056.01 4.122.06 2.908.134 4.683 1.912 4.818 4.818.05 1.066.06 1.405.06 4.122 0 2.717-.01 3.056-.06 4.122-.135 2.902-1.906 4.684-4.818 4.818-1.066.05-1.405.06-4.122.06-2.717 0-3.056-.01-4.122-.06-2.906-.134-4.683-1.912-4.818-4.818C2.01 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122C2.195 4.914 3.97 3.134 6.878 3c1.066-.05 1.405-.06 4.122-.06m0-1.802c-2.765 0-3.11.012-4.19.061-3.87.178-6.015 2.32-6.192 6.192C1.612 8.89 1.6 9.235 1.6 12c0 2.765.012 3.11.061 4.19.178 3.868 2.316 6.013 6.192 6.192 1.08.049 1.425.061 4.19.061 2.765 0 3.11-.012 4.19-.061 3.863-.178 6.015-2.32 6.192-6.192.048-1.08.06-1.425.06-4.19 0-2.765-.012-3.11-.06-4.19-.174-3.862-2.32-6.013-6.192-6.192C15.11.212 14.765.198 12 .198zm0 3.055a5.747 5.747 0 100 11.494A5.747 5.747 0 0012 3.253zm0 9.478a3.731 3.731 0 110-7.462 3.731 3.731 0 010 7.462zm7.318-9.709a1.343 1.343 0 10-2.686 0 1.343 1.343 0 002.686 0z" />
    </svg>
  );
}

export function SocialGrid({ props }: SectionComponentProps) {
  const {
    headline = "Follow Along",
    subheadline,
    instagramHandle = "@yourbusiness",
    instagramUrl = "#",
    images = PLACEHOLDER_IMAGES,
  } = props as SocialGridProps;

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-stagger-child]",
        { opacity: 0, scale: 0.94 },
        {
          opacity: 1, scale: 1, duration: 0.45, stagger: 0.06, ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 80%", toggleActions: "play none none none" },
        },
      );
    }, el);

    return () => ctx.revert();
  }, []);

  const displayed = images.slice(0, 9);

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-background" id="social">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          {headline && (
            <h2 className="text-2xl font-bold text-foreground mb-1">{headline}</h2>
          )}
          {subheadline && (
            <p className="text-muted-foreground text-sm">{subheadline}</p>
          )}
        </div>

        <div className="grid grid-cols-3 gap-1.5 mb-6">
          {displayed.map((img, i) => (
            <a
              key={i}
              href={instagramUrl}
              target={instagramUrl !== "#" ? "_blank" : undefined}
              rel="noopener noreferrer"
              data-stagger-child
              className="group relative aspect-square overflow-hidden rounded-[calc(var(--radius)*0.5)] bg-muted"
              aria-label={img.alt ?? `Instagram photo ${i + 1}`}
            >
              {img.src ? (
                <img
                  src={img.src}
                  alt={img.alt ?? ""}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <span className="text-muted-foreground/40 text-2xl">📷</span>
                </div>
              )}
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <InstagramIcon />
                </span>
              </div>
            </a>
          ))}
        </div>

        <div className="flex justify-center">
          <a
            href={instagramUrl}
            target={instagramUrl !== "#" ? "_blank" : undefined}
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-[var(--radius)] border border-border px-6 py-2.5 text-sm font-semibold text-foreground hover:border-primary/50 hover:text-primary transition-colors"
          >
            <InstagramIcon />
            Follow {instagramHandle}
          </a>
        </div>
      </div>
    </section>
  );
}
